/**
 * AppliedChips — tiny inline confirmations of writes Aria just did.
 *
 * Apple cards (one click to commit) are GONE. The chat now behaves like
 * Build mode: the founder talks, the agent does. We just render a small
 * green chip per applied change so the founder sees "✓ README patched"
 * inline under Aria's reply.
 *
 * IRREVERSIBLE actions (send email · post social · push code · charge)
 * still go through the Validation Modal at the atom-shipment step. They
 * never show up here.
 */
import React from 'react';
import { Check } from 'lucide-react';

const KIND_LABEL = {
  revenue_plan_patch:    'Revenue Plan patched',
  monetize_module_patch: 'Doc patched',
  loop_atom:             'Atom added',
  canonical_decision:    'Decision sealed',
  loop_question_resolve: 'Question answered',
  task_update:           'Task patched',
};

function detail(change) {
  const se = change.side_effect || {};
  if (!change.applied) return change.error || (se.reason || 'not applied');
  if (se.kind === 'revenue_plan_patch')    return (se.patched_keys || []).join(' · ');
  if (se.kind === 'monetize_module_patch') return `${se.module || 'doc'} · ${(se.patched_keys || []).join(' · ')}`;
  if (se.kind === 'loop_atom')             return 'added to your Execution Plan';
  if (se.kind === 'canonical_decision')    return se.topic || 'sealed';
  if (se.kind === 'loop_question_resolve') return 'blocker unlocked';
  if (se.kind === 'task_update')           return (se.patched_keys || []).join(' · ');
  return 'done';
}

export default function ProposalCards({ proposals }) {
  // Backwards compat: accept either `proposals` (legacy pending shape) or
  // `applied_changes` via the same prop. New backend always sends applied.
  if (!proposals || proposals.length === 0) return null;
  return (
    <div data-testid="applied-chips" style={{
      display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8,
    }}>
      {proposals.map((c, i) => {
        const ok = c.applied !== false;   // default true when prop comes from `applied_changes`
        return (
          <div
            key={c.proposal_id || i}
            data-testid={`applied-${c.proposal_id || i}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 999,
              background: ok ? '#ECFDF5' : '#FEF2F2',
              border: `1px solid ${ok ? '#A7F3D0' : '#FECACA'}`,
              fontSize: 11, color: ok ? '#065F46' : '#7F1D1D',
              fontWeight: 600,
            }}>
            <Check size={11} />
            <span style={{ fontWeight: 700 }}>
              {KIND_LABEL[c.kind] || c.kind || 'Change'}
            </span>
            <span style={{ opacity: 0.75 }}>· {detail(c)}</span>
          </div>
        );
      })}
    </div>
  );
}
